import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Card, CardContent, CardHeader, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Paper, Select, MenuItem,
    FormControl, InputLabel, SelectChangeEvent, Tooltip, CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, VpnKey } from '@mui/icons-material';
import { IUser, RoleName, ICreateUserRequest, IChangeUserRoleRequest, IChangeUserPasswordRequest } from './userManagement';
import authService from '../../components/api-authorization/AuthorizeService';
import Loader from '../Loader';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

const roleDisplayNames: { [key: string]: string } = {
    'Admin': 'Администратор',
    'TerminalManager': 'Управление терминалами',
    'RefundManager': 'Управление возвратами',
    'RefundViewer': 'Просмотр возвратов',
};

const getRoleDisplayName = (role: string): string => {
    return roleDisplayNames[role] || role;
};

const UserManagementPage: React.FC = () => {
    const [users, setUsers] = useState<IUser[]>([]);
    const [roles, setRoles] = useState<RoleName[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);

    const [createUserModal, setCreateUserModal] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const [newEmail, setNewEmail] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [newRole, setNewRole] = useState<string>('');

    const [changeRoleModal, setChangeRoleModal] = useState(false);
    const [editingUser, setEditingUser] = useState<IUser | null>(null);
    const [selectedRoleForEdit, setSelectedRoleForEdit] = useState<string>('');

    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [changingPasswordUser, setChangingPasswordUser] = useState<IUser | null>(null);
    const [newPasswordForChange, setNewPasswordForChange] = useState<string>('');

    const [deleteUserModal, setDeleteUserModal] = useState(false);
    const [deletingUser, setDeletingUser] = useState<IUser | null>(null);

    const { toast, showError, showSuccess, handleClose } = useToast();

    const toggleCreateUserModal = () => setCreateUserModal(!createUserModal);
    const toggleChangeRoleModal = () => setChangeRoleModal(!changeRoleModal);
    const toggleChangePasswordModal = () => setChangePasswordModal(!changePasswordModal);
    const toggleDeleteUserModal = () => setDeleteUserModal(!deleteUserModal);

    const fetchUsersAndRoles = useCallback(async () => {
        setLoading(true);
        try {
            const user = await authService.getUser();
            setCurrentUserId(user?.profile?.sub || null);

            const usersResponse = await fetch('/api/users');
            if (!usersResponse.ok) {
                const errorMsg = await formatApiError(usersResponse, 'Не удалось загрузить пользователей');
                throw new Error(errorMsg);
            }
            const usersData: IUser[] = await usersResponse.json();
            setUsers(usersData);

            const rolesResponse = await fetch('/api/users/roles');
            if (!rolesResponse.ok) {
                const errorMsg = await formatApiError(rolesResponse, 'Не удалось загрузить роли');
                throw new Error(errorMsg);
            }
            const rolesData: RoleName[] = await rolesResponse.json();
            setRoles(rolesData);

            if (rolesData.length > 0) {
                setNewRole(currentRole => currentRole || rolesData[0]);
            }
        } catch (err: any) {
            showError(err.message || 'Произошла ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    }, [showError]);

    useEffect(() => {
        fetchUsersAndRoles();
    }, [fetchUsersAndRoles]);

    const handleCreateUser = async (event: FormEvent) => {
        event.preventDefault();
        if (!newEmail || !newPassword || !newRole) {
            showError('Все поля обязательны для заполнения');
            return;
        }

        setCreatingUser(true);
        try {
            const response = await fetch('/api/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newEmail, password: newPassword, role: newRole } as ICreateUserRequest),
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось создать пользователя');
                throw new Error(errorMsg);
            }
            const result = await response.json();
            showSuccess(result.Message || 'Пользователь успешно создан. Приглашение отправлено на email.');
            setNewEmail('');
            setNewPassword('');
            toggleCreateUserModal();
            await fetchUsersAndRoles();
        } catch (err: any) {
            showError(err.message || 'Произошла ошибка при создании пользователя');
        } finally {
            setCreatingUser(false);
        }
    };

    const openChangeRoleModal = (user: IUser) => {
        setEditingUser(user);
        setSelectedRoleForEdit(user.roles[0] || (roles.length > 0 ? roles[0] : ''));
        toggleChangeRoleModal();
    };

    const handleChangeRole = async (event: FormEvent) => {
        event.preventDefault();
        if (!editingUser || !selectedRoleForEdit) return;
        try {
            const response = await fetch(`/api/users/${editingUser.id}/role`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newRole: selectedRoleForEdit } as IChangeUserRoleRequest),
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось изменить роль пользователя');
                throw new Error(errorMsg);
            }
            const result = await response.json();
            showSuccess(result.Message || 'Роль пользователя успешно обновлена');
            toggleChangeRoleModal();
            await fetchUsersAndRoles();
        } catch (err: any) {
            showError(err.message || 'Произошла ошибка при изменении роли');
        }
    };

    const openChangePasswordModal = (user: IUser) => {
        setChangingPasswordUser(user);
        setNewPasswordForChange('');
        toggleChangePasswordModal();
    };

    const handleChangePassword = async (event: FormEvent) => {
        event.preventDefault();
        if (!changingPasswordUser || !newPasswordForChange) return;
        try {
            const response = await fetch(`/api/users/${changingPasswordUser.id}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword: newPasswordForChange } as IChangeUserPasswordRequest),
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось изменить пароль');
                throw new Error(errorMsg);
            }
            const result = await response.json();
            showSuccess(result.Message || 'Пароль пользователя успешно изменен');
            toggleChangePasswordModal();
        } catch (err: any) {
            showError(err.message || 'Произошла ошибка при изменении пароля');
        }
    };

    const openDeleteUserModal = (user: IUser) => {
        setDeletingUser(user);
        toggleDeleteUserModal();
    };

    const handleDeleteUser = async () => {
        if (!deletingUser) return;
        try {
            const response = await fetch(`/api/users/${deletingUser.id}`, { method: 'DELETE' });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось удалить пользователя');
                throw new Error(errorMsg);
            }
            const result = await response.json();
            showSuccess(result.Message || 'Пользователь успешно удален');
            toggleDeleteUserModal();
            await fetchUsersAndRoles();
        } catch (err: any) {
            showError(err.message || 'Произошла ошибка при удалении пользователя');
        }
    };

    if (loading) return <Loader />;

    return (
        <Box>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Card>
                <CardHeader
                    title="Управление пользователями"
                    action={
                        <Button variant="contained" startIcon={<Add />} onClick={toggleCreateUserModal}>
                            Создать
                        </Button>
                    }
                />
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Роли</TableCell>
                                    <TableCell align="right">Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.roles.map(getRoleDisplayName).join(', ')}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Изменить роль">
                                                <span>
                                                    <IconButton onClick={() => openChangeRoleModal(user)} disabled={user.id === currentUserId}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Сменить пароль">
                                                <IconButton onClick={() => openChangePasswordModal(user)}>
                                                    <VpnKey fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Удалить">
                                                <span>
                                                    <IconButton onClick={() => openDeleteUserModal(user)} disabled={user.id === currentUserId} color="error">
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Create User Modal */}
            <Dialog open={createUserModal} onClose={creatingUser ? undefined : toggleCreateUserModal} fullWidth maxWidth="sm">
                <DialogTitle>Создать нового пользователя</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleCreateUser} sx={{ mt: 2 }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            required
                            fullWidth
                            margin="normal"
                            disabled={creatingUser}
                        />
                        <TextField
                            label="Пароль"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            fullWidth
                            margin="normal"
                            disabled={creatingUser}
                        />
                        <FormControl fullWidth margin="normal" disabled={creatingUser}>
                            <InputLabel>Роль</InputLabel>
                            <Select value={newRole} label="Роль" onChange={(e: SelectChangeEvent) => setNewRole(e.target.value)}>
                                {roles.map((role) => (
                                    <MenuItem key={role} value={role}>{getRoleDisplayName(role)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {creatingUser && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, color: 'text.secondary' }}>
                            <CircularProgress size={20} />
                            <span>Создание пользователя и отправка приглашения...</span>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleCreateUserModal} disabled={creatingUser}>Отмена</Button>
                    <Button
                        onClick={handleCreateUser}
                        variant="contained"
                        disabled={creatingUser}
                        startIcon={creatingUser ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {creatingUser ? 'Создание...' : 'Создать'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Change Role Modal */}
            {editingUser && (
                <Dialog open={changeRoleModal} onClose={toggleChangeRoleModal} fullWidth maxWidth="sm">
                    <DialogTitle>Изменить роль для {editingUser.email}</DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleChangeRole} sx={{ mt: 2 }}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Новая роль</InputLabel>
                                <Select value={selectedRoleForEdit} label="Новая роль" onChange={(e: SelectChangeEvent) => setSelectedRoleForEdit(e.target.value)}>
                                    {roles.map((role) => (
                                        <MenuItem key={role} value={role}>{getRoleDisplayName(role)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleChangeRoleModal}>Отмена</Button>
                        <Button onClick={handleChangeRole} variant="contained">Сохранить</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Change Password Modal */}
            {changingPasswordUser && (
                <Dialog open={changePasswordModal} onClose={toggleChangePasswordModal} fullWidth maxWidth="sm">
                    <DialogTitle>Сменить пароль для {changingPasswordUser.email}</DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleChangePassword} sx={{ mt: 2 }}>
                            <TextField label="Новый пароль" type="password" value={newPasswordForChange} onChange={(e) => setNewPasswordForChange(e.target.value)} required fullWidth margin="normal" />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleChangePasswordModal}>Отмена</Button>
                        <Button onClick={handleChangePassword} variant="contained">Сменить</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Delete User Modal */}
            {deletingUser && (
                <Dialog open={deleteUserModal} onClose={toggleDeleteUserModal}>
                    <DialogTitle>Подтверждение удаления</DialogTitle>
                    <DialogContent>
                        Вы уверены, что хотите удалить пользователя "<b>{deletingUser.email}</b>"?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleDeleteUserModal}>Отмена</Button>
                        <Button onClick={handleDeleteUser} color="error" variant="contained">Удалить</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default UserManagementPage;
